import { Helmet } from 'react-helmet';
import {Box, Grid2, Typography} from '@mui/material';
import Header from '../../layout/Header';
import Footer from '../../layout/Footer';
import {useContext, useEffect, useRef} from 'react';
import CustomContext from "../../contexts/CustomContext";

export default function PrivacyPolicy() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const context = useContext(CustomContext);

  useEffect(() => {
    if (context?.setIsLightBg) {
      context.setIsLightBg(true); // Force light background context
    }

    // Optional: Reset to false on unmount
    return () => {
      if (context?.setIsLightBg) {
        context.setIsLightBg(false);
      }
    };
  }, [context]);

  return (
    <Grid2
      container
      flexDirection="column"
      sx={{ position: 'relative', minHeight: '100vh', bgcolor: 'secondary.lighter', color: 'primary.main' }}
    >
      <Header />

      {/* MAIN SCROLLABLE CONTENT */}
      <Box component="main" sx={{ width: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box ref={scrollContainerRef} sx={{ height: '100vh', scrollSnapType: 'y mandatory', scrollBehavior: 'smooth', overflowY: 'auto' }}>
          <Helmet>
            <title>Privacy Policy - SHEKR</title>
            <meta name="description" content="Understand how we collect, use, and protect your data." />
          </Helmet>

          <Box sx={{ pt: '6rem', maxWidth: '800px', mx: 'auto', px: 2 }}>
            <Typography variant="h4" sx={{ mb: 3 }}><strong>Politique de confidentialité</strong></Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>1. INTRODUCTION</strong></Typography>
            <Typography paragraph><strong>Last Update</strong><br />Last updated on 9th December 2025.</Typography>
            <Typography paragraph><strong>Scope of Application</strong><br />This Privacy Policy explains how we collect, use, share, and protect your
              personal data when you use our platform. We want you to feel confident in how
              your information is handled, whether you are here to discover new creators, shop
              unique finds, earn from what you share or create your own space in the
              community.</Typography>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>2. Who We Are</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>Welcome to SHEKR, the social media that gives back to its community.</Typography>
              <Typography paragraph>
                If you live in the European Economic Area (EEA), the United Kingdom, or
                Switzerland, SHEKR is the controller of your personal data. That means we are
                responsible for deciding how and why your data is used. If you ever have
                questions about this policy or your data, you can reach us at{' '}
              </Typography>
              <Typography paragraph>You enter into a contract with us when you confirm that you accept these Terms or when you use the Platform. Consequently, you may not use our services if you do not accept our Terms. However, additional terms or policies may apply if you wish to access special features, publish specific types of content, or use the Platform for professional or commercial purposes. These additional terms or policies will apply to you from the moment you use such services or agree to the relevant conditions.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>3. What Information We Collect</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                We collect information that you give us directly, such as when you create an
                account, upload content, interact with other users, or complete a purchase.
                This includes your email address, username, profile picture, and any
                messages or content you submit.
              </Typography>
              <Typography paragraph>
                We also automatically collect certain data about how you interact with
                SHEKR. This includes your device type, IP address, operating system, app
                version, and how you use the app like pages viewed, clicks, scrolling
                behavior, and error logs. Location data may be collected based on your IP
                address or device settings, but only at a general level unless you have
                explicitly given permission for more precise data.
              </Typography>
              <Typography paragraph>
                In some cases, we also receive data from third parties. For example, if you
                complete a transaction via a payment provider like Stripe, we may receive
                certain information from those platforms to complete your profile or
                transaction.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>4. How and Why We Use Your Data</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>We use your data to provide and improve SHEKR. This includes things like
                creating and managing your account, delivering content and products,
                enabling purchases, and personalizing your experience. For example, we may
                show you creators or products that match your interests, based on how you
                use the platform.
              </Typography>
              <Typography paragraph>
                We also use your data to improve SHEKR like fixing bugs, testing new
                features, and measuring performance. Sometimes we use it for security
                reasons, like preventing fraud or ensuring that our community remains safe.
              </Typography>
              <Typography paragraph>
                We rely on several legal bases to process your data. In some cases, it’s
                necessary for us to fulfill our contract with you (like when you create an
                account or make a purchase). In other cases, we process your data based on
                your consent (such as for personalized recommendations or marketing). We may
                also rely on our legitimate interests, such as improving the service or
                protecting users, as long as those interests do not override your rights.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>5. Data Collection Technologies and Similar Tools</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>We use data collection technologies, SDKs, and pixels to help power SHEKR. Some of these are
                essential to make the platform work like keeping you logged in or remembering
                what's in your cart. Others help us understand how you use SHEKR so we can
                make it better, or personalize your experience by recommending relevant
                creators or products. You can learn more about how we use data collection technologies in our{' '}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>6. International Transfers</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>We never sell your personal data. We only share it in limited situations.
                SHEKR is based in France, but we work with partners around the world. If your
                data is transferred outside the EEA, we ensure it’s protected through measures
                like Standard Contractual Clauses approved by the European Commission or
                equivalent legal safeguards.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>7. How Long We Keep Your Data</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>We only keep your data for as long as we need it to provide our services, or
                as required by law. For example, we may keep your account information while
                your account is active and for a short period afterward. Data collection technology data is
                typically stored for up to 13 months. In cases involving fraud or legal
                issues, we may retain certain data for longer.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>8. Your Rights and Choices</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                Under the General Data Protection Regulation (GDPR), you have a number of
                important rights. You can ask to access the data we hold about you, request
                corrections, or ask us to delete it. You also have the right to withdraw
                your consent at any time, object to certain uses, and request that your data
                be transferred to another provider.
              </Typography>
              <Typography paragraph>
                If you want to exercise any of these rights, just contact us at hello@shekr.com
                We will respond as soon as we can, and always within the timelines required by law.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>9. Children’s Privacy</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>SHEKR is not intended for users under the age of 16. We do not knowingly
                collect personal data from children under this age. If we learn that we have
                collected data from someone under 16, we will delete it promptly.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>10. Changes to This Policy</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                We may update this Privacy Policy from time to time. If we make significant
                changes, we’ll let you know through the app or by email before they take
                effect. The latest version will always be available here.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}><strong>11. How to Contact Us</strong></Typography>
            <Box sx={{ pl: 2 }}>
              <Typography paragraph>
                If you have any questions about this policy, your data, or your rights, please
                email us at hello@shekr.com. We are here to help.
              </Typography>
            </Box>
          </Box>

          <Footer />
        </Box>
      </Box>
    </Grid2>
  );
}
